import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Button, DatePicker, Descriptions, Empty, Form, Modal, Select, Space, Table, Tag, Typography, message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import {
  createAuditVisit, getAuditVisits, getAvailableRatingTemplates, getProjects, getRatingSetup, getUsers,
  setRatingSetup,
} from '../../services/api';
import { getApiError } from '../../utils/apiError';
import ChecklistScreen from './ChecklistScreen';

const { Text } = Typography;
const { RangePicker } = DatePicker;

interface AuditProject { id: string; code: string; name: string; }
interface Setup { template_id: string | null; template: string | null; can_manage: boolean; visits_exist: boolean; }
interface Visit {
  id: string; visit_id: string; status: string; start_date: string; end_date: string;
  lead_auditor: string; additional_auditors: string[]; reviewer: string | null; my_roles: string[];
}
interface UserRow { id: string; full_name: string; is_active: boolean; }

const STATUS_COLOUR: { [status: string]: string } = {
  OPEN: 'blue', SUBMITTED: 'gold', UNDER_REVIEW: 'purple', RETURNED: 'orange', COMPLETED: 'green',
};
const ROLE_LABEL: { [role: string]: string } = {
  LEAD_AUDITOR: 'Lead auditor', AUDITOR: 'Auditor', REVIEWER: 'Reviewer', MANAGER: 'Manager',
};

const VisitsTab: React.FC = () => {
  const [projects, setProjects] = useState<AuditProject[]>([]);
  const [projectId, setProjectId] = useState<string | undefined>();
  const [setup, setSetup] = useState<Setup | null>(null);
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openVisit, setOpenVisit] = useState<string | null>(null);

  const [templates, setTemplates] = useState<{ id: string; label: string }[]>([]);
  const [pickedTemplate, setPickedTemplate] = useState<string | undefined>();
  const [assigning, setAssigning] = useState(false);

  const [users, setUsers] = useState<UserRow[]>([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    getProjects()
      .then((res) => {
        const rows = res.data.filter((p: any) => p.engagement_mode === 'AUDIT' && p.audit_pattern === 'VISIT');
        setProjects(rows);
        if (rows.length > 0) setProjectId(rows[0].id);
      })
      .catch((err) => setError(getApiError(err, 'Failed to load projects.')));
  }, []);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError('');
    try {
      const [setupRes, visitsRes] = await Promise.all([getRatingSetup(projectId), getAuditVisits(projectId)]);
      setSetup(setupRes.data);
      setVisits(visitsRes.data);
      if (setupRes.data.can_manage && !setupRes.data.visits_exist) {
        const tRes = await getAvailableRatingTemplates();
        setTemplates(tRes.data);
        setPickedTemplate(setupRes.data.template_id || undefined);
      }
    } catch (err) {
      setError(getApiError(err, 'Failed to load the audit visits.'));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const assign = async () => {
    if (!projectId || !pickedTemplate) return;
    setAssigning(true);
    try {
      await setRatingSetup(projectId, pickedTemplate);
      message.success('Template assigned to the project.');
      load();
    } catch (err) {
      message.error(getApiError(err, 'Could not assign the template.'));
    } finally {
      setAssigning(false);
    }
  };

  const openCreate = async () => {
    form.resetFields();
    setCreateOpen(true);
    if (users.length === 0) {
      try {
        const res = await getUsers();
        setUsers(res.data.filter((u: UserRow) => u.is_active));
      } catch (err) {
        message.error(getApiError(err, 'Could not load the user list.'));
      }
    }
  };

  const submitVisit = async () => {
    if (!projectId) return;
    const values = await form.validateFields();
    setCreating(true);
    try {
      await createAuditVisit(projectId, {
        start_date: values.dates[0].format('YYYY-MM-DD'),
        end_date: values.dates[1].format('YYYY-MM-DD'),
        lead_auditor_id: values.lead_auditor_id,
        additional_auditor_ids: values.additional_auditor_ids || [],
        reviewer_id: values.reviewer_id,
      });
      message.success('Visit raised.');
      setCreateOpen(false);
      load();
    } catch (err) {
      message.error(getApiError(err, 'Could not raise the visit.'));
    } finally {
      setCreating(false);
    }
  };

  const userOptions = users.map((u) => ({ value: u.id, label: u.full_name }));
  const canRaise = !!setup?.can_manage && !!setup?.template_id;

  if (openVisit) {
    return <ChecklistScreen visitId={openVisit} onBack={() => { setOpenVisit(null); load(); }} />;
  }

  if (projects.length === 0 && !error) {
    return <Empty description="No visit-based quality audit project is available to you." />;
  }

  return (
    <div>
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}
      <Space style={{ marginBottom: 16 }} wrap>
        <Text strong>Project</Text>
        <Select
          style={{ minWidth: 320 }}
          value={projectId}
          onChange={setProjectId}
          options={projects.map((p) => ({ value: p.id, label: `${p.code} - ${p.name}` }))}
        />
      </Space>

      {setup && (
        <Descriptions bordered size="small" column={1} style={{ marginBottom: 16, maxWidth: 720 }}>
          <Descriptions.Item label="Rating template">
            {setup.template ? <Text strong>{setup.template}</Text> : <Text type="warning">Not assigned yet</Text>}
          </Descriptions.Item>
          {setup.can_manage && !setup.visits_exist && (
            <Descriptions.Item label={setup.template ? 'Change template' : 'Assign template'}>
              <Space wrap>
                <Select
                  style={{ minWidth: 340 }}
                  placeholder="Choose an active template"
                  value={pickedTemplate}
                  onChange={setPickedTemplate}
                  options={templates.map((t) => ({ value: t.id, label: t.label }))}
                  notFoundContent="No active template yet"
                />
                <Button
                  type="primary" onClick={assign} loading={assigning}
                  disabled={!pickedTemplate || pickedTemplate === setup.template_id}
                >
                  Assign
                </Button>
              </Space>
            </Descriptions.Item>
          )}
          {setup.visits_exist && (
            <Descriptions.Item label="Note">
              <Text type="secondary">The template is fixed once visits have been raised.</Text>
            </Descriptions.Item>
          )}
        </Descriptions>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
        <Text type="secondary">Visits of this project that you are part of.</Text>
        {canRaise && (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Visit</Button>
        )}
      </div>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={visits}
        pagination={false}
        locale={{ emptyText: 'No visits yet' }}
        columns={[
          { title: 'Visit', dataIndex: 'visit_id' },
          { title: 'From', dataIndex: 'start_date' },
          { title: 'To', dataIndex: 'end_date' },
          {
            title: 'Auditors',
            render: (_: any, v: Visit) => [v.lead_auditor + ' (lead)', ...v.additional_auditors].join(', '),
          },
          { title: 'Reviewer', dataIndex: 'reviewer' },
          {
            title: 'Status', dataIndex: 'status',
            render: (s: string) => <Tag color={STATUS_COLOUR[s] || 'default'}>{s.replace('_', ' ')}</Tag>,
          },
          {
            title: 'Your part', dataIndex: 'my_roles',
            render: (roles: string[]) => roles.map((r) => <Tag key={r}>{ROLE_LABEL[r] || r}</Tag>),
          },
          {
            title: '', key: 'open',
            render: (_: any, v: Visit) => <Button size="small" onClick={() => setOpenVisit(v.id)}>Open</Button>,
          },
        ]}
      />

      <Modal
        title="Raise a visit"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={submitVisit}
        okText="Raise visit"
        confirmLoading={creating}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="dates" label="Visit dates" rules={[{ required: true, message: 'Choose the visit dates' }]}>
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="lead_auditor_id" label="Lead auditor" rules={[{ required: true, message: 'Choose the lead auditor' }]}>
            <Select showSearch optionFilterProp="label" options={userOptions} />
          </Form.Item>
          <Form.Item name="additional_auditor_ids" label="Additional auditors">
            <Select mode="multiple" optionFilterProp="label" options={userOptions} />
          </Form.Item>
          <Form.Item
            name="reviewer_id" label="Reviewer"
            extra="The reviewer cannot be one of the auditors and need not be on the project team."
            rules={[{ required: true, message: 'Choose the reviewer' }]}
          >
            <Select showSearch optionFilterProp="label" options={userOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default VisitsTab;
